import { Controller } from "@hotwired/stimulus"

// Linear-shaped typeahead for the rail's triage chips (assign, type, part, priority),
// the bulk-bar assign picker, and Change sender. Search, highlight, Enter and digit
// shortcuts live here; each field still owns its own form and submit path.
// Change sender also uses the optional fixed-position menu and create-from-query row.
// A fixed menu can also be held inside a column (`within`, a selector for the trigger's ancestor),
// for a trigger in a column that clips: the bulk bar's Move picker stays inside .app__list.
//
// Single-select closes and submits. Multi-select stays open and submits — a request
// type is a hidden input flipped enabled/disabled, so this controller never builds
// DOM. The chips region re-renders from the response; the menu and trigger sit
// outside it, so checked state and "Clear all" are moved here.
const VIEWPORT_MARGIN = 8
const TRIGGER_GAP = 6

export default class extends Controller {
  static targets = ["menu", "field", "query", "empty", "clear", "name", "create", "createCaption", "createDivider"]
  static values = { fixed: Boolean, remote: Boolean, within: String }

  connect() {
    this.closeOnOutside = this.closeOnOutside.bind(this)
    this.onKeydown = this.onKeydown.bind(this)
    this.onPointerOver = this.onPointerOver.bind(this)
    this.closeOnScroll = this.closeOnScroll.bind(this)
  }

  disconnect() {
    this.removeListeners()
  }

  toggleMenu() {
    this.menuTarget.hidden ? this.open() : this.close()
  }

  open() {
    this.menuTarget.hidden = false
    this.button.setAttribute("aria-expanded", "true")
    this.resetFilter()
    if (this.fixedValue) this.positionMenu()
    this.highlight(this.navigableOptions[0])
    this.focusQuery()
    document.addEventListener("click", this.closeOnOutside)
    document.addEventListener("keydown", this.onKeydown)
    this.menuTarget.addEventListener("pointerover", this.onPointerOver)
    if (this.fixedValue) {
      this.ignoreScroll = true
      document.addEventListener("scroll", this.closeOnScroll, true)
      requestAnimationFrame(() => { this.ignoreScroll = false })
    }
  }

  close() {
    this.menuTarget.hidden = true
    this.button.setAttribute("aria-expanded", "false")
    this.resetFilter()
    this.clearHighlight()
    this.removeListeners()
  }

  select(event) {
    const chosen = event.currentTarget
    this.menuTarget.querySelectorAll("[role='menuitemradio']").forEach((option) => {
      option.setAttribute("aria-checked", option === chosen ? "true" : "false")
    })
    this.fieldTarget.value = event.params.value
    if (this.hasNameTarget) this.nameTarget.value = event.params.name || ""
    this.close()
    this.submitForm()
  }

  // Driven by both a menu option and a chip's remove button — same action, same value
  // param, so the two can never disagree about what a click means.
  toggle(event) {
    const field = this.fieldFor(event.params.value)
    if (!field) return

    this.setSelected(field, field.disabled)
    this.submitForm()
    if (!this.menuTarget.hidden) this.focusQuery()
  }

  clear() {
    this.fieldTargets.forEach((field) => this.setSelected(field, false))
    this.submitForm()
    if (!this.menuTarget.hidden) this.focusQuery()
  }

  filter() {
    const query = this.typedQuery

    // A remote menu is already the answer to this query — remote_options_controller re-fetched it
    // — so hiding rows here would be filtering the result a second time against a query the
    // server read more generously than `includes` does.
    if (!this.remoteValue) {
      this.options.forEach((option) => {
        if (this.isCreateOption(option)) return

        option.hidden = query !== "" && !this.optionLabel(option).includes(query)
      })
    }

    this.refreshDividers(query)
    this.refreshCreate()
    this.refreshEmpty(this.visibleOptions.length)
    this.refreshDigits()
    this.highlight(this.navigableOptions[0])
  }

  // A remote menu's rows are replaced under this controller as the reader types, so everything it
  // keeps per row — the digit shortcuts, the highlight, whether "Use <address>" would duplicate a
  // row that has now arrived — has to be rebuilt against the set that actually landed.
  refresh() {
    if (this.menuTarget.hidden) return

    this.refreshCreate()
    this.refreshDigits()
    this.highlight(this.navigableOptions[0], { scroll: false })
  }

  onKeydown(event) {
    if (this.menuTarget.hidden) return

    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
      this.button.focus()
      return
    }

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.moveHighlight(1)
      return
    }

    if (event.key === "ArrowUp") {
      event.preventDefault()
      this.moveHighlight(-1)
      return
    }

    if (event.key === "Enter") {
      event.preventDefault()
      this.highlighted?.click()
      return
    }

    const index = this.digitIndex(event.key)
    if (index === null) return

    const option = this.visibleOptions[index]
    if (!option) return

    event.preventDefault()
    option.click()
  }

  onPointerOver(event) {
    const option = event.target.closest(".dropdown__option")
    if (option && this.menuTarget.contains(option) && !option.hidden) this.highlight(option)
  }

  setSelected(field, selected) {
    field.disabled = !selected
    this.optionFor(field.dataset.value)?.setAttribute("aria-checked", String(selected))
  }

  submitForm() {
    this.refreshClear()
    this.element.closest("form").requestSubmit()
  }

  // "Clear all" sits outside the region the response swaps, so it is shown and hidden here.
  refreshClear() {
    if (!this.hasClearTarget) return

    this.clearTarget.hidden = !this.fieldTargets.some((field) => !field.disabled)
  }

  // Compared as strings on purpose. Stimulus type-casts action params, so a request type's
  // numeric id arrives here as the Number 3 while dataset.value is always the String "3".
  fieldFor(value) {
    return this.fieldTargets.find((field) => field.dataset.value === String(value))
  }

  optionFor(value) {
    return this.menuTarget.querySelector(`[data-typeahead-value-param="${String(value)}"]`)
  }

  resetFilter() {
    if (this.hasQueryTarget) this.queryTarget.value = ""
    this.options.forEach((option) => {
      option.hidden = this.isCreateOption(option)
    })
    if (this.hasCreateDividerTarget) this.createDividerTarget.hidden = true
    this.refreshDividers("")
    this.refreshEmpty(this.visibleOptions.length)
    this.refreshDigits()
  }

  refreshDividers(query) {
    this.menuTarget.querySelectorAll(".dropdown__divider").forEach((divider) => {
      if (this.hasCreateDividerTarget && divider === this.createDividerTarget) return

      divider.hidden = query !== ""
    })
  }

  refreshEmpty(visibleCount) {
    if (!this.hasEmptyTarget) return

    this.emptyTarget.hidden = !(this.options.length > 0 && visibleCount === 0)
  }

  refreshDigits() {
    this.options.forEach((option) => option.removeAttribute("data-typeahead-digit"))
    this.visibleOptions.slice(0, 10).forEach((option, index) => {
      option.dataset.typeaheadDigit = index === 9 ? "0" : String(index + 1)
    })
  }

  // `scroll: false` after a remote refill. The first row of a list that has just been replaced is
  // already at the top, so there is nothing to scroll to — and scrolling anyway can move an
  // ancestor, which for a fixed menu is a scroll event that closes it mid-search.
  highlight(option, { scroll = true } = {}) {
    this.clearHighlight()
    if (!option) return

    option.classList.add("is-active")
    if (scroll) option.scrollIntoView({ block: "nearest" })
  }

  clearHighlight() {
    this.menuTarget.querySelectorAll(".dropdown__option.is-active").forEach((option) => {
      option.classList.remove("is-active")
    })
  }

  moveHighlight(delta) {
    const options = this.navigableOptions
    if (options.length === 0) return

    const current = options.indexOf(this.highlighted)
    const start = current === -1 ? (delta > 0 ? -1 : 0) : current
    const next = (start + delta + options.length) % options.length
    this.highlight(options[next])
  }

  focusQuery() {
    this.queryTarget.focus()
  }

  closeOnOutside(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  refreshCreate() {
    if (!this.hasCreateTarget) return

    const parsed = this.parseCreateQuery()
    const exists = parsed && this.options.some((option) => {
      return !this.isCreateOption(option) && !option.hidden &&
        option.dataset.typeaheadValueParam === parsed.email
    })
    const show = Boolean(parsed && !exists)

    this.createTarget.hidden = !show
    if (this.hasCreateDividerTarget) {
      const othersVisible = this.options.some((option) => !this.isCreateOption(option) && !option.hidden)
      this.createDividerTarget.hidden = !(show && othersVisible)
    }
    if (!show) return

    this.createTarget.dataset.typeaheadValueParam = parsed.email
    this.createTarget.dataset.typeaheadNameParam = parsed.name || ""
    if (this.hasCreateCaptionTarget) this.createCaptionTarget.textContent = parsed.email
  }

  parseCreateQuery() {
    const trimmed = this.queryTarget.value.trim()
    const angled = trimmed.match(/^(.+?)\s*<([^<>@\s]+@[^<>@\s]+)>$/i)
    if (angled) return { name: angled[1].trim(), email: angled[2].toLowerCase() }
    if (/^[^@\s]+@[^@\s]+$/.test(trimmed)) return { name: "", email: trimmed.toLowerCase() }

    return null
  }

  isCreateOption(option) {
    return this.hasCreateTarget && option === this.createTarget
  }

  // Below the trigger, or above it when there is no room below, and then clamped on all four sides:
  // a trigger scrolled partly out of view must not drag the menu's last rows off the screen with it.
  positionMenu() {
    const menu = this.menuTarget
    menu.style.position = "fixed"
    menu.style.right = "auto"

    // The room the menu has, for a stylesheet that caps its width by it (see .list__bulk-move-menu).
    const bounds = this.horizontalBounds()
    menu.style.setProperty("--typeahead-room", `${bounds.right - bounds.left}px`)

    const anchor = this.button.getBoundingClientRect()
    const panel = menu.getBoundingClientRect()
    const below = anchor.bottom + TRIGGER_GAP
    let top = below + panel.height > window.innerHeight - VIEWPORT_MARGIN
      ? anchor.top - panel.height - TRIGGER_GAP
      : below
    top = Math.min(top, window.innerHeight - VIEWPORT_MARGIN - panel.height)
    const left = Math.min(anchor.left, bounds.right - panel.width)

    menu.style.top = `${Math.max(VIEWPORT_MARGIN, top)}px`
    menu.style.left = `${Math.max(bounds.left, left)}px`
  }

  // The viewport, or the column named by `within` where the trigger has one, less the margin.
  horizontalBounds() {
    const column = this.withinValue ? this.element.closest(this.withinValue) : null
    const rect = column?.getBoundingClientRect()
    const left = Math.max(0, rect?.left ?? 0) + VIEWPORT_MARGIN
    const right = Math.min(window.innerWidth, rect?.right ?? window.innerWidth) - VIEWPORT_MARGIN
    return { left, right }
  }

  closeOnScroll(event) {
    if (this.ignoreScroll) return
    if (this.menuTarget.contains(event.target)) return

    this.close()
  }

  removeListeners() {
    document.removeEventListener("click", this.closeOnOutside)
    document.removeEventListener("keydown", this.onKeydown)
    document.removeEventListener("scroll", this.closeOnScroll, true)
    this.menuTarget.removeEventListener("pointerover", this.onPointerOver)
  }

  digitIndex(key) {
    if (key === "0") return 9
    if (key >= "1" && key <= "9") return Number(key) - 1
    return null
  }

  optionLabel(option) {
    return option.textContent.trim().toLowerCase()
  }

  get typedQuery() {
    return this.queryTarget.value.trim().toLowerCase()
  }

  get options() {
    return Array.from(this.menuTarget.querySelectorAll("[data-typeahead-option]"))
  }

  get visibleOptions() {
    return this.options.filter((option) => !option.hidden)
  }

  get navigableOptions() {
    return this.visibleOptions.concat(this.visibleClearButton)
  }

  get visibleClearButton() {
    if (!this.hasClearTarget || this.clearTarget.hidden) return []

    const button = this.clearTarget.querySelector(".dropdown__option")
    return button ? [ button ] : []
  }

  get highlighted() {
    return this.menuTarget.querySelector(".dropdown__option.is-active")
  }

  get button() {
    return this.element.querySelector("[aria-haspopup]")
  }
}
